import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  ChevronLeft,
  Trash2,
  Users,
  ChevronUp,
  ChevronDown,
  Send,
  AlertCircle,
  ExternalLink,
  Loader2,
  Filter
} from "lucide-react";
import { PpcFarmSellersMasterDocumentClient } from "../../types/ppcFarmTypes";
import { CohortFilterCriteria } from "./CohortFilterForm";
import { cohortReviewService } from "../../services/cohortReviewService";
import { addressValidationService } from "../../services/addressValidationService";

// Seller with document ID for display
interface SellerWithId extends PpcFarmSellersMasterDocumentClient {
  id: string;
}

// Sort configuration
interface SortConfig {
  field: keyof SellerWithId | 'sellerName' | 'location' | 'productCount' | 'ratingCount' | 'brandCount' | 'rating';
  direction: 'asc' | 'desc';
}

interface CohortReviewPageProps {
  sellers: SellerWithId[];
  totalCount: number;
  filters: CohortFilterCriteria;
  onBackToFilters: () => void;
  onSellerDelete: (sellerId: string) => void;
  onSendForReview: (data: {
    creatorName: string;
    cohortNotes: string;
    cohortNumber: number;
    sellerIds: string[];
    validationResults: object[];
    filters: object;
  }) => Promise<void>;
  isValidationInProgress?: boolean;
  isSendingForReview?: boolean;
}

const CohortReviewPage: React.FC<CohortReviewPageProps> = ({
  sellers,
  totalCount,
  filters,
  onBackToFilters,
  onSellerDelete,
  onSendForReview
}) => {
  // Local state for sorting
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'sellerName',
    direction: 'asc'
  });

  // Form state for Phase 1 enhancement
  const [creatorName, setCreatorName] = useState('');
  const [cohortNotes, setCohortNotes] = useState('');
  const [cohortNumber, setCohortNumber] = useState<number>(1);
  const [isLoadingCohortNumber, setIsLoadingCohortNumber] = useState(false);
  const [isSendingForReview, setIsSendingForReview] = useState(false);
  const [validationResults, setValidationResults] = useState<object[]>([]);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  // Load next cohort number on component mount
  useEffect(() => {
    const loadNextCohortNumber = async () => {
      try {
        setIsLoadingCohortNumber(true);
        const nextNumber = await cohortReviewService.getNextCohortNumber();
        setCohortNumber(nextNumber);
      } catch (error) {
        console.error('Failed to load next cohort number:', error);
        // Keep default value of 1 if loading fails
      } finally {
        setIsLoadingCohortNumber(false);
      }
    };

    loadNextCohortNumber();
  }, []);

  // Handle sending cohort for review
  const handleSendForReview = async () => {
    try {
      setFormErrors([]);
      setIsSendingForReview(true);

      // First, run address validation with current sellers
      console.log('🔍 Running address validation before sending for review...');
      const sellerIds = sortedSellers.map(s => s.id);
      
      const validationResponse = await addressValidationService.validateAddresses(sellerIds);
      
      if (!validationResponse.success) {
        setFormErrors(['Address validation failed. Please try again.']);
        return;
      }

      // Store validation results
      const currentValidationResults = validationResponse.results || [];
      setValidationResults(currentValidationResults);

      // Validate form inputs with fresh validation results
      const errors = cohortReviewService.validateInput({
        creatorName,
        cohortNotes,
        cohortNumber,
        sellerIds,
        validationResults: currentValidationResults,
        filters
      });

      if (errors.length > 0) {
        setFormErrors(errors);
        return;
      }

      // Send for review with current data
      await onSendForReview({
        creatorName: creatorName.trim(),
        cohortNotes: cohortNotes.trim(),
        cohortNumber,
        sellerIds,
        validationResults: currentValidationResults,
        filters
      });

      console.log('✅ Cohort sent for review successfully');

    } catch (error) {
      console.error('Error sending cohort for review:', error);
      setFormErrors([error instanceof Error ? error.message : 'Failed to send cohort for review']);
    } finally {
      setIsSendingForReview(false);
    }
  };

  // Sort sellers based on current sort configuration
  const sortedSellers = useMemo(() => {
    return [...sellers].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.field) {
        case 'sellerName':
          aValue = a.sellerData?.sellerName || a.queryable_sellerName || '';
          bValue = b.sellerData?.sellerName || b.queryable_sellerName || '';
          break;
        case 'location':
          const aAddress = a.queryable_address;
          const bAddress = b.queryable_address;
          aValue = [aAddress?.city, aAddress?.state, aAddress?.country].filter(Boolean).join(', ');
          bValue = [bAddress?.city, bAddress?.state, bAddress?.country].filter(Boolean).join(', ');
          break;
        case 'productCount':
          aValue = a.queryable_productCount || a.sellerData?.totalStorefrontAsins?.[1] || 0;
          bValue = b.queryable_productCount || b.sellerData?.totalStorefrontAsins?.[1] || 0;
          break;
        case 'ratingCount':
          aValue = a.queryable_ratingCount || 0;
          bValue = b.queryable_ratingCount || 0;
          break;
        case 'brandCount':
          aValue = a.queryable_brandCount || 0;
          bValue = b.queryable_brandCount || 0;
          break;
        case 'rating':
          aValue = a.queryable_rating || 0;
          bValue = b.queryable_rating || 0;
          break;
        default:
          aValue = a[sortConfig.field as keyof SellerWithId];
          bValue = b[sortConfig.field as keyof SellerWithId];
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Handle numeric comparison
      const numA = Number(aValue) || 0;
      const numB = Number(bValue) || 0;
      const comparison = numA - numB;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [sellers, sortConfig]);

  // Generate user-friendly filter summary
  const activeFilters = useMemo(() => {
    const filterList: string[] = [];

    // Country filter
    if (filters.country) {
      const countryNames: { [key: string]: string } = {
        'US': 'United States',
        'CN': 'China',
        'CA': 'Canada',
        'UK': 'United Kingdom',
        'AU': 'Australia',
        'DE': 'Germany',
        'FR': 'France',
        'IT': 'Italy',
        'ES': 'Spain'
      };
      filterList.push(`Country: ${countryNames[filters.country] || filters.country}`);
    }

    // Product count range
    if (filters.minProducts || filters.maxProducts) {
      if (filters.minProducts && filters.maxProducts) {
        filterList.push(`Products: ${filters.minProducts} - ${parseInt(filters.maxProducts).toLocaleString()}`);
      } else if (filters.minProducts) {
        filterList.push(`Min Products: ${filters.minProducts}`);
      } else if (filters.maxProducts) {
        filterList.push(`Max Products: ${parseInt(filters.maxProducts).toLocaleString()}`);
      }
    }

    // Rating count range
    if (filters.minRatings || filters.maxRatings) {
      if (filters.minRatings && filters.maxRatings) {
        filterList.push(`Ratings: ${parseInt(filters.minRatings).toLocaleString()} - ${parseInt(filters.maxRatings).toLocaleString()}`);
      } else if (filters.minRatings) {
        filterList.push(`Min Ratings: ${parseInt(filters.minRatings).toLocaleString()}`);
      } else if (filters.maxRatings) {
        filterList.push(`Max Ratings: ${parseInt(filters.maxRatings).toLocaleString()}`);
      }
    }

    // Brand count range
    if (filters.minBrands || filters.maxBrands) {
      if (filters.minBrands && filters.maxBrands) {
        filterList.push(`Brands: ${filters.minBrands} - ${filters.maxBrands}`);
      } else if (filters.minBrands) {
        filterList.push(`Min Brands: ${filters.minBrands}`);
      } else if (filters.maxBrands) {
        filterList.push(`Max Brands: ${filters.maxBrands}`);
      }
    }

    // Rating percentage range
    if (filters.minRating || filters.maxRating) {
      if (filters.minRating && filters.maxRating) {
        filterList.push(`Rating: ${filters.minRating}% - ${filters.maxRating}%`);
      } else if (filters.minRating) {
        filterList.push(`Min Rating: ${filters.minRating}%`);
      } else if (filters.maxRating) {
        filterList.push(`Max Rating: ${filters.maxRating}%`);
      }
    }

    // Buy Box filters
    if (filters.minAvgBuyBoxCompetitors || filters.maxAvgBuyBoxCompetitors) {
      if (filters.minAvgBuyBoxCompetitors && filters.maxAvgBuyBoxCompetitors) {
        filterList.push(`Avg Competitors: ${filters.minAvgBuyBoxCompetitors} - ${filters.maxAvgBuyBoxCompetitors}`);
      } else if (filters.minAvgBuyBoxCompetitors) {
        filterList.push(`Min Avg Competitors: ${filters.minAvgBuyBoxCompetitors}`);
      } else if (filters.maxAvgBuyBoxCompetitors) {
        filterList.push(`Max Avg Competitors: ${filters.maxAvgBuyBoxCompetitors}`);
      }
    }

    if (filters.minBuyBoxNewOwnershipRate || filters.maxBuyBoxNewOwnershipRate) {
      if (filters.minBuyBoxNewOwnershipRate && filters.maxBuyBoxNewOwnershipRate) {
        filterList.push(`New Buy Box Rate: ${filters.minBuyBoxNewOwnershipRate}% - ${filters.maxBuyBoxNewOwnershipRate}%`);
      } else if (filters.minBuyBoxNewOwnershipRate) {
        filterList.push(`Min New Buy Box Rate: ${filters.minBuyBoxNewOwnershipRate}%`);
      } else if (filters.maxBuyBoxNewOwnershipRate) {
        filterList.push(`Max New Buy Box Rate: ${filters.maxBuyBoxNewOwnershipRate}%`);
      }
    }

    if (filters.minBuyBoxUsedOwnershipRate || filters.maxBuyBoxUsedOwnershipRate) {
      if (filters.minBuyBoxUsedOwnershipRate && filters.maxBuyBoxUsedOwnershipRate) {
        filterList.push(`Used Buy Box Rate: ${filters.minBuyBoxUsedOwnershipRate}% - ${filters.maxBuyBoxUsedOwnershipRate}%`);
      } else if (filters.minBuyBoxUsedOwnershipRate) {
        filterList.push(`Min Used Buy Box Rate: ${filters.minBuyBoxUsedOwnershipRate}%`);
      } else if (filters.maxBuyBoxUsedOwnershipRate) {
        filterList.push(`Max Used Buy Box Rate: ${filters.maxBuyBoxUsedOwnershipRate}%`);
      }
    }

    // FBA filter
    if (filters.queryable_hasFBA) {
      if (filters.queryable_hasFBA === 'true') {
        filterList.push('FBA Sellers Only');
      } else if (filters.queryable_hasFBA === 'false') {
        filterList.push('Non-FBA Sellers Only');
      }
    }

    // Boolean filters
    if (filters.excludeFromTargetLedger) {
      filterList.push('Excluding Target Ledger sellers');
    }

    if (filters.excludeSellersFromChina) {
      filterList.push('Excluding China shippers');
    }

    return filterList;
  }, [filters]);

  // Handle sort column click
  const handleSort = (field: SortConfig['field']) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Render sort icon
  const renderSortIcon = (field: SortConfig['field']) => {
    if (sortConfig.field !== field) {
      return <ChevronUp className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="h-4 w-4 text-blue-600" />
      : <ChevronDown className="h-4 w-4 text-blue-600" />;
  };

  // Format address for display
  const formatAddress = (address: any) => {
    if (!address) return "N/A";
    const parts = [];
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.country) parts.push(address.country);
    return parts.length > 0 ? parts.join(", ") : "N/A";
  };

  // Format rating for display
  const formatRating = (rating: number | null | undefined) => {
    if (!rating) return 'N/A';
    const ratingNum = typeof rating === 'number' ? rating : parseFloat(String(rating));
    return isNaN(ratingNum) ? 'N/A' : `${ratingNum.toFixed(1)}%`;
  };

  // Open seller page in new tab
  const openSellerPage = (sellerId: string) => {
    if (!sellerId) return;
    const url = `https://www.amazon.com/sp?seller=${sellerId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={onBackToFilters}
            className="flex items-center"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Filters
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Review Cohort
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Review and manage your seller cohort before address validation
            </p>
          </div>
        </div>
      </div>

      {/* Summary Card - Compact horizontal layout */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center text-lg">
            <Users className="h-5 w-5 mr-2" />
            Cohort Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Compact Number and Filters in One Row */}
          <div className="flex items-center gap-6">
            {/* Seller Count - Compact */}
            <div className="flex items-center gap-3 pr-6 border-r border-gray-200 dark:border-gray-700">
              <div className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                {sellers.length.toLocaleString()}
              </div>
              <div className="text-base text-gray-600 dark:text-gray-400">
                Sellers
              </div>
            </div>

            {/* Filter Summary - Horizontal Pills */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
              </div>
              {activeFilters.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {activeFilters.map((filter, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                    >
                      {filter}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No filters applied
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Phase 1: Cohort Review Form */}
      <Card>
        <CardHeader>
          <CardTitle>Send Cohort for Review</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Form Errors */}
          {formErrors.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-md p-3">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                    Please fix the following errors:
                  </h3>
                  <ul className="mt-2 text-sm text-red-700 dark:text-red-300 list-disc list-inside">
                    {formErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Your Name - Required */}
            <div className="space-y-2">
              <Label htmlFor="creatorName" className="text-sm font-medium">
                Your Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="creatorName"
                type="text"
                placeholder="Enter your name"
                value={creatorName}
                onChange={(e) => setCreatorName(e.target.value)}
                disabled={isSendingForReview}
                className="w-full"
              />
            </div>

            {/* Cohort Number - Required, Pre-filled */}
            <div className="space-y-2">
              <Label htmlFor="cohortNumber" className="text-sm font-medium">
                Cohort Number <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="cohortNumber"
                  type="number"
                  min="1"
                  value={cohortNumber}
                  onChange={(e) => setCohortNumber(parseInt(e.target.value) || 1)}
                  disabled={isSendingForReview || isLoadingCohortNumber}
                  className="w-full"
                />
                {isLoadingCohortNumber && (
                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                )}
              </div>
            </div>

            {/* Cohort Notes - Optional */}
            <div className="space-y-2">
              <Label htmlFor="cohortNotes" className="text-sm font-medium">
                Cohort Notes
              </Label>
              <Textarea
                id="cohortNotes"
                placeholder="Optional notes about this cohort"
                value={cohortNotes}
                onChange={(e) => setCohortNotes(e.target.value)}
                disabled={isSendingForReview}
                className="w-full min-h-[38px] resize-none"
                rows={1}
              />
            </div>
          </div>

          {/* Send for Review Button */}
          <div className="flex justify-center pt-4">
            <Button
              onClick={handleSendForReview}
              disabled={sortedSellers.length === 0 || isSendingForReview || !creatorName.trim()}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {isSendingForReview ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending for Review...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Cohort for Review ({sortedSellers.length.toLocaleString()} Sellers)
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sellers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Seller Details</CardTitle>
        </CardHeader>
        <CardContent>
          {sortedSellers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                No sellers in this cohort.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th
                      className="text-left p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => handleSort('sellerName')}
                    >
                      <div className="flex items-center">
                        Company Name
                        {renderSortIcon('sellerName')}
                      </div>
                    </th>
                    <th
                      className="text-left p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => handleSort('location')}
                    >
                      <div className="flex items-center">
                        Location
                        {renderSortIcon('location')}
                      </div>
                    </th>
                    <th
                      className="text-left p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => handleSort('productCount')}
                    >
                      <div className="flex items-center">
                        Products
                        {renderSortIcon('productCount')}
                      </div>
                    </th>
                    <th
                      className="text-left p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => handleSort('ratingCount')}
                    >
                      <div className="flex items-center">
                        Ratings
                        {renderSortIcon('ratingCount')}
                      </div>
                    </th>
                    <th
                      className="text-left p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => handleSort('brandCount')}
                    >
                      <div className="flex items-center">
                        Brands
                        {renderSortIcon('brandCount')}
                      </div>
                    </th>
                    <th
                      className="text-left p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => handleSort('rating')}
                    >
                      <div className="flex items-center">
                        Rating %
                        {renderSortIcon('rating')}
                      </div>
                    </th>
                    <th className="text-left p-3">FBA Status</th>
                    <th className="text-left p-3">In Target Ledger</th>
                    <th className="text-center p-3 w-20">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSellers.map((seller) => (
                    <tr key={seller.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="p-3">
                        <div className="font-medium flex items-center gap-2">
                          <span>{seller.sellerData?.sellerName || seller.queryable_sellerName || 'N/A'}</span>
                          {seller.sellerId && (
                            <button
                              onClick={() => openSellerPage(seller.sellerId)}
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                              title="View seller page on Amazon"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {seller.sellerId}
                        </div>
                      </td>
                      <td className="p-3">
                        {formatAddress(seller.queryable_address)}
                      </td>
                      <td className="p-3">
                        {(seller.queryable_productCount || seller.sellerData?.totalStorefrontAsins?.[1] || 0).toLocaleString()}
                      </td>
                      <td className="p-3">
                        {(seller.queryable_ratingCount || 0).toLocaleString()}
                      </td>
                      <td className="p-3">
                        {seller.queryable_brandCount || 0}
                      </td>
                      <td className="p-3">
                        {formatRating(seller.queryable_rating)}
                      </td>
                      <td className="p-3">
                        <Badge
                          variant="secondary"
                          className={
                            seller.queryable_hasFBA
                              ? "bg-blue-50 text-blue-700 dark:bg-blue-900/10 dark:text-blue-400"
                              : "bg-gray-50 text-gray-600 dark:bg-gray-900/10 dark:text-gray-400"
                          }
                        >
                          {seller.queryable_hasFBA ? "Yes" : "No"}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge
                          variant="secondary"
                          className={
                            seller.queryable_isInTargetLedger
                              ? "bg-green-50 text-green-700 dark:bg-green-900/10 dark:text-green-400"
                              : "bg-gray-50 text-gray-600 dark:bg-gray-900/10 dark:text-gray-400"
                          }
                        >
                          {seller.queryable_isInTargetLedger ? "Yes" : "No"}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onSellerDelete(seller.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/10"
                          title="Remove seller from cohort"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CohortReviewPage;